/* eslint-disable react/button-has-type */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import type { LoadError } from '@react-pdf-viewer/core';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { getAllPagesNumbers, printPlugin } from '@react-pdf-viewer/print';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

import { axiosInstance } from 'api';
import { Button } from '../Button';
import { Icon } from '../Icon';
import type { PdfPresenterExtendProps } from './PdfPresenter';

type PdfViewerProps = {
	//
} & PdfPresenterExtendProps;

const isPublicPdfUrl = (url: string) =>
	url.includes('GoogleAccessId') ||
	url.includes('storage.googleapis.com') ||
	url.includes('Signature=') ||
	url.includes('/proxy/gs/');

export const PdfViewer = ({ title, src, externalSrc }: PdfViewerProps) => {
	const { t } = useTranslation();

	const currentSrc = src || externalSrc;
	const [ready, setReady] = useState(false);

	useEffect(() => {
		setReady(false);
	}, [currentSrc]);

	const getFilePluginInstance = getFilePlugin({
		fileNameGenerator: () => title || 'document.pdf',
	});
	const printPluginInstance = printPlugin({
		enableShortcuts: true,
		setPages: getAllPagesNumbers,
	});
	const zoomPluginInstance = zoomPlugin();

	const Download = getFilePluginInstance.Download as any;
	const Print = printPluginInstance.Print as any;
	const ZoomIn = zoomPluginInstance.ZoomIn as any;
	const ZoomOut = zoomPluginInstance.ZoomOut as any;
	const ZoomPopover = zoomPluginInstance.ZoomPopover as any;

	// Let Rsbuild emit the worker with the same pdfjs-dist version as the viewer.
	// A fixed file from public/ can silently drift from the API version and fail at runtime.
	const workerUrl = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).href;

	const transformGetDocumentParams = useCallback(
		(options: any) => {
			const isPublic = typeof currentSrc === 'string' && isPublicPdfUrl(currentSrc);
			const authHeader = axiosInstance.defaults.headers.common.Authorization;

			return {
				...options,
				// GCS returns Access-Control-Allow-Origin: *; withCredentials: true causes browser CORS failure.
				withCredentials: !isPublic,
				isEvalSupported: false,
				enableScripting: false,
				httpHeaders: {
					...(options.httpHeaders || {}),
					...(!isPublic && authHeader ? { Authorization: String(authHeader) } : {}),
				},
			};
		},
		[currentSrc]
	);

	const renderError = (error: LoadError) => {
		return (
			<div className="flex flex-column align-items-center justify-content-center p-4 text-center gap-2 text-gray-700">
				<Icon name="exclamation-circle" className="text-amber-500 text-3xl" />
				<div className="font-semibold text-base">
					{t('userPanel.userFiles.pdfLoadError', 'Nie udało się załadować pliku PDF')}
				</div>
				<div className="text-sm text-gray-500">
					{error.name === 'MissingPDFException'
						? t(
								'userPanel.userFiles.pdfNotFound',
								'Plik nie został znaleziony na serwerze lub link wygasł.'
							)
						: error.message}
				</div>
				{(externalSrc || currentSrc) && (
					<div className="mt-2">
						<a
							href={externalSrc || currentSrc}
							target="_blank"
							rel="noreferrer"
							className="p-button p-component p-button-sm p-button-outlined"
						>
							<span className="p-button-label">
								{t('userPanel.userFiles.openInNewTab', 'Otwórz plik w nowej karcie')}
							</span>
						</a>
					</div>
				)}
			</div>
		);
	};

	return (
		<>
			<div className="flex items-center gap-2 justify-content-between align-items-center flex-wrap">
				<div className="flex gap-2 align-items-center">
					<ZoomOut>
						{(props: any) => (
							<Button
								label={t('misc.zoomOut', 'Oddal')}
								onClick={props.onClick}
								icon="minus"
								iconOnly
								variantSize="sm"
								disabled={!ready}
							/>
						)}
					</ZoomOut>
					<ZoomPopover />
					<ZoomIn>
						{(props: any) => (
							<Button
								label={t('misc.zoomIn', 'Przybliż')}
								onClick={props.onClick}
								icon="plus"
								iconOnly
								variantSize="sm"
								disabled={!ready}
							/>
						)}
					</ZoomIn>
				</div>
				<div className="flex gap-2 align-items-center">
					<Print>
						{(props: any) => (
							<Button
								label={t('misc.print', 'Drukuj')}
								onClick={props.onClick}
								icon="print"
								variantSize="sm"
								disabled={!ready}
							/>
						)}
					</Print>
					<Download>
						{(props: any) => (
							<Button
								label={t('misc.downloadFile', 'Pobierz')}
								onClick={props.onClick}
								icon="download"
								variantSize="sm"
								disabled={!ready}
							/>
						)}
					</Download>
				</div>
			</div>

			<div className="flex-1 h-full overflow-hidden">
				<Worker workerUrl={workerUrl}>
					{currentSrc && (
						<Viewer
							key={currentSrc}
							fileUrl={currentSrc}
							defaultScale={SpecialZoomLevel.PageWidth}
							transformGetDocumentParams={transformGetDocumentParams}
							plugins={[getFilePluginInstance, printPluginInstance, zoomPluginInstance]}
							renderError={renderError}
							onDocumentLoad={() => {
								setReady(true);
							}}
						/>
					)}
				</Worker>
			</div>
		</>
	);
};
